import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import { downloadFile } from '../lib/downloadFile';

// Shared attachment-viewer dialog used by the supplier-facing surfaces
// (SupplierResponse for brief attachments, SupplierCompliance for the
// compliance doc previews). Images render inline via <img>; everything
// else (PDFs / Office docs) routes through a native <iframe>.
//
// Image viewer was originally a fixed-size modal with the image dumped
// inline – small thumbnails sat in a tiny corner of a huge black panel.
// This version: wider modal cap, padded gallery-style container, image
// centred with object-fit:contain so it scales to whichever dimension
// runs out of room first (never upscales beyond natural size), and a
// soft border + shadow so small images look intentional rather than
// stranded. Documents keep the 70vh iframe treatment.
//
// Props:
//   doc      – { name, url, mime?, fileName? }. `name` is the display title
//              (may be a rename with no extension); `mime` and `fileName` are
//              the reliable image signals. Passing null hides the modal.
//   onClose  – close handler (Escape, backdrop click, ×, Close button)
//   onDownload – optional override for the Download button. Default is
//                an anchor click with the doc's name as the suggested
//                filename.

const IMAGE_EXT_RE = /\.(jpg|jpeg|png|gif|webp|bmp|svg|heic|heif)(\?|$)/i;
const isImageFile = (name) => IMAGE_EXT_RE.test(String(name || ''));

// Decide "is this an image" from every signal, not just the display name – a
// renamed attachment ("Hei Hei", no extension) would otherwise fall to the
// black iframe path. mime_type is the most reliable; the original file name and
// the storage URL both usually still carry the real extension.
const docIsImage = (doc) =>
  String(doc?.mime || '').toLowerCase().startsWith('image/')
  || isImageFile(doc?.fileName)
  || isImageFile(doc?.name)
  || isImageFile(doc?.url);

const HTML_EXT_RE = /\.html?(\?|$)/i;
// Hosted-form submissions are stored as .html files. The storage object's
// served content-type is unreliable (the browser shows the source in an
// iframe, and a cross-origin <a download> saves it wrong), so HTML docs are
// fetched and rendered/downloaded from their content instead of their URL.
const docIsHtml = (doc) =>
  String(doc?.mime || '').toLowerCase() === 'text/html'
  || HTML_EXT_RE.test(String(doc?.fileName || ''))
  || HTML_EXT_RE.test(String(doc?.url || ''));

const ensureHtmlExt = (name) => {
  const n = String(name || 'document').trim() || 'document';
  return /\.html?$/i.test(n) ? n : `${n}.html`;
};

const defaultDownload = (doc) => {
  if (!doc?.url) return;
  // Blob download – <a download> is ignored cross-origin and would navigate
  // to the Supabase storage URL instead (see lib/downloadFile).
  downloadFile(doc.url, doc.fileName || doc.name || '');
};

export default function AttachmentViewerModal({ doc, onClose, onDownload }) {
  const isHtml = docIsHtml(doc);
  const docUrl = doc?.url || null;
  // Fetch HTML content so it can be rendered via srcDoc (renders regardless of
  // the served content-type) and downloaded as a real .html file.
  const [htmlContent, setHtmlContent] = useState(null);
  const [htmlError, setHtmlError] = useState(false);
  useEffect(() => {
    if (!isHtml || !docUrl) { setHtmlContent(null); setHtmlError(false); return undefined; }
    let cancelled = false;
    setHtmlContent(null); setHtmlError(false);
    fetch(docUrl)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); })
      .then(t => { if (!cancelled) setHtmlContent(t); })
      .catch(() => { if (!cancelled) setHtmlError(true); });
    return () => { cancelled = true; };
  }, [isHtml, docUrl]);

  if (!doc) return null;
  const showImage = docIsImage(doc);
  const showHtml = !showImage && isHtml;
  const handleDownload = () => {
    if (onDownload) { onDownload(doc); return; }
    // For HTML, download the fetched content directly (the stored object's
    // content-type is unreliable, so a URL-based download saves it wrong).
    if (showHtml && htmlContent != null) {
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = ensureHtmlExt(doc.fileName || doc.name);
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
      return;
    }
    defaultDownload(doc);
  };

  // Download as PDF – for the HTML documents (signed hosted forms), open the
  // rendered content in a print window and trigger print; the document's
  // stylesheet already paginates for print (cover page-break, @page margins),
  // and the browser's print dialog offers "Save as PDF". Same print-window
  // approach used by the quote-comparison export.
  const handleDownloadPdf = () => {
    if (!(showHtml && htmlContent != null)) { defaultDownload(doc); return; }
    const w = window.open('', '_blank', 'noopener,noreferrer');
    if (!w) return; // popup blocked
    w.document.open();
    w.document.write(htmlContent);
    w.document.close();
    const go = () => { try { w.focus(); w.print(); } catch { /* user can print manually */ } };
    if (w.document.readyState === 'complete') setTimeout(go, 350);
    else w.onload = () => setTimeout(go, 350);
  };
  return (
    <Modal
      open
      onClose={onClose}
      maxWidth={showImage ? 1100 : 900}
      stripe={false}
      // Images: shrink the modal to the image (no large panel around a small
      // picture). Documents keep the full-width iframe panel.
      fitContent={showImage}
      ariaLabel={`View ${doc.name}`}>
      <Modal.Header title={doc.name} onClose={onClose} />
      <Modal.Body>
        {showImage ? (
          // Image hugs its own size – no black gallery panel around it (the
          // modal is fitContent, so it shrinks to the image). Caps at the
          // viewport height and 100% width so large images scale down but
          // never upscale a small thumbnail. A soft border + shadow keeps it
          // looking intentional on the light modal.
          <div className="flex items-center justify-center">
            <img
              src={doc.url}
              alt={doc.name}
              style={{
                maxWidth: '100%',
                maxHeight: 'calc(85vh - 200px)',
                width: 'auto',
                height: 'auto',
                display: 'block',
                borderRadius: 8,
                boxShadow: '0 2px 12px rgba(45,48,35,0.12)',
                border: '1px solid #E6EADB',
              }}
            />
          </div>
        ) : showHtml ? (
          // Hosted-form HTML – rendered from fetched content via srcDoc (a
          // white document, so a light panel), sandboxed (static HTML/CSS only).
          <div className="rounded-md overflow-hidden border border-[#E6EADB]" style={{ background: '#ffffff' }}>
            {htmlError ? (
              <p className="font-['Metropolis'] text-[13px] text-[#E0290A] p-6 text-center">Couldn’t load this document. Try Download.</p>
            ) : htmlContent == null ? (
              <p className="font-['Metropolis'] text-[13px] text-fusion-mid-grey p-6 text-center">Loading…</p>
            ) : (
              <iframe
                title={doc.name}
                srcDoc={htmlContent}
                sandbox=""
                style={{ width: '100%', height: '75vh', border: 0, background: '#ffffff', display: 'block' }} />
            )}
          </div>
        ) : (
          <div className="rounded-md overflow-hidden" style={{ background: '#0a0a0a' }}>
            <iframe
              src={doc.url}
              title={doc.name}
              style={{ width: '100%', height: '75vh', border: 0, background: '#0a0a0a', display: 'block' }} />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-[#51534A] hover:text-fusion-mid-grey bg-transparent border-0 cursor-pointer">
            Close
          </button>
          {showHtml && (
            <button
              type="button"
              onClick={handleDownloadPdf}
              className="bg-white border border-[#2E8FA1] rounded-xl text-[#2E8FA1] font-['Metropolis'] text-xs font-semibold px-4 py-2 hover:bg-[#2E8FA1]/10 transition-colors">
              Download PDF
            </button>
          )}
          <button
            type="button"
            onClick={handleDownload}
            className="bg-white border border-line rounded-xl text-fusion-mid-grey font-['Metropolis'] text-xs font-semibold px-4 py-2 hover:border-[#DADCCD] transition-colors">
            {showHtml ? 'Download HTML' : 'Download'}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
